<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue'

type Placement = 'top' | 'bottom' | 'left' | 'right' | 'left-middle' | 'right-middle'

const props = defineProps({
  modelValue: { type: Boolean, default: false }, // allow manual control
  content: { type: String, required: false },
  placement: { type: String as () => Placement, default: 'top' },
  target: { type: Object as () => HTMLElement, required: true },
  offset: { type: Number, default: 8 },
  showArrow: { type: Boolean, default: true },
  tooltipId: { type: String, required: false }
})

const emit = defineEmits(['update:modelValue'])

const tooltipRef = ref<HTMLElement | null>(null)
const isVisible = ref(false)
const resizeObserver = ref<ResizeObserver | null>(null)
const needsReposition = ref(false)
const rafId = ref<number | null>(null)

const computedPlacement = computed<Placement>(() => {
  if (['top', 'bottom', 'left', 'right', 'left-middle', 'right-middle'].includes(props.placement)) {
    return props.placement as Placement
  }
  return 'top'
})

const positionTooltip = () => {
  if (!tooltipRef.value || !props.target) return

  const triggerRect = props.target.getBoundingClientRect()
  const tooltipRect = tooltipRef.value.getBoundingClientRect()
  const scrollY = window.scrollY || window.pageYOffset
  const scrollX = window.scrollX || window.pageXOffset
  const offset = props.offset

  let top = 0
  let left = 0

  const placement = computedPlacement.value

  switch (placement) {
    case 'bottom':
      top = triggerRect.bottom + offset
      left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
      break
    case 'left-middle':
      top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
      left = triggerRect.left - tooltipRect.width - offset
      break
    case 'right-middle':
      top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
      left = triggerRect.right + offset
      break
    case 'left':
      top = triggerRect.top;// + (triggerRect.height - tooltipRect.height) / 2
      left = triggerRect.left - tooltipRect.width - offset
      break
    case 'right':
      top = triggerRect.top;// + (triggerRect.height - tooltipRect.height) / 2
      left = triggerRect.right + offset
      break
    case 'top':
    default:
      top = triggerRect.top - tooltipRect.height - offset
      left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
      break
  }

  // clamp to viewport
  const viewportWidth = document.documentElement.clientWidth
  const viewportHeight = document.documentElement.clientHeight

  const maxLeft = viewportWidth - tooltipRect.width - 4 + scrollX
  const maxTop = viewportHeight - tooltipRect.height - 4 + scrollY

  top = Math.max(scrollY + 4, Math.min(top + scrollY, maxTop))
  left = Math.max(scrollX + 4, Math.min(left + scrollX, maxLeft))

  tooltipRef.value.style.top = `${top}px`
  tooltipRef.value.style.left = `${left}px`
}

const schedulePosition = (doubleFrame = false) => {
  if (!isVisible.value && !doubleFrame) return
  if (rafId.value !== null) {
    cancelAnimationFrame(rafId.value)
  }
  const run = () => {
    rafId.value = null
    positionTooltip()
  }
  if (doubleFrame) {
    rafId.value = requestAnimationFrame(() => {
      rafId.value = requestAnimationFrame(run)
    })
    return
  }
  rafId.value = requestAnimationFrame(run)
}

const show = async () => {
  isVisible.value = true
  emit('update:modelValue', true)
  await nextTick()
  schedulePosition(true)
  if (needsReposition.value) {
    schedulePosition(true)
    needsReposition.value = false
  }
  if (resizeObserver.value && tooltipRef.value) {
    resizeObserver.value.observe(tooltipRef.value)
  }
}

const hide = () => {
  isVisible.value = false
  emit('update:modelValue', false)
  if (resizeObserver.value && tooltipRef.value) {
    resizeObserver.value.unobserve(tooltipRef.value)
  }
}

const handleResizeOrScroll = () => {
  if (!isVisible.value) {
    needsReposition.value = true
    return
  }
  schedulePosition(true)
}

onMounted(() => {
  if (props.modelValue) {
    show()
  }
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver.value = new ResizeObserver(() => {
      handleResizeOrScroll()
    })
    if (props.target) {
      resizeObserver.value.observe(props.target)
    }
  }
  window.addEventListener('resize', handleResizeOrScroll, true)
  window.addEventListener('scroll', handleResizeOrScroll, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResizeOrScroll, true)
  window.removeEventListener('scroll', handleResizeOrScroll, true)
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
    resizeObserver.value = null
  }
  if (rafId.value !== null) {
    cancelAnimationFrame(rafId.value)
    rafId.value = null
  }
})

watch(
  () => props.target,
  (nextTarget, prevTarget) => {
    if (!resizeObserver.value) return
    if (prevTarget) {
      resizeObserver.value.unobserve(prevTarget)
    }
    if (nextTarget) {
      resizeObserver.value.observe(nextTarget)
    }
  }
)

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      show()
    } else {
      hide()
    }
  }
)
</script>

<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-show="isVisible"
        ref="tooltipRef"
        :id="tooltipId"
        :class="['sai-tooltip', `sai-tooltip--${computedPlacement}`, { 'sai-tooltip--arrow': props.showArrow }]"
        role="tooltip"
        aria-live="polite"
      >
        <slot>{{ content }}</slot>
      </div>
    </transition>
  </teleport>
</template>
